import { createAsyncThunk } from '@reduxjs/toolkit';
import Cookies, { get } from 'js-cookie';
import i18n from 'i18next';
import {
  checkEmailExistUrl,
  loginAppleMobile,
  loginAppleUrl,
  loginGoogleUrl,
  loginUrl,
  logoutUrl,
  registerUrl,
  resetPasswordUrl,
  settings,
  token,
  userUrl,
} from '../../../settings';
import { sendRequest } from '../../../store/api';
import { getStateAndToken } from '../../../utils/helpers';
import { PasswordType, ProfileType } from '../../../pages/Profile/types/types';
import { CreateUserType, GuestUser, LoginUserType } from './userTypes';
import { clearUserSlice, setIsNewUser } from './userSlice';
import { clearAppSlice, setSnackbar } from '../app/appSlice';
import { clearOrderSlice } from '../order/orderSlice';
import { clearTeamSlice } from '../team/teamSlice';
import { clearFileSlice } from '../file/fileSlice';
let isGuestUserCreationInProgress = false;
let isGetUserCreationInProgress = false;

export const checkEmailExist = createAsyncThunk('user/checkEmail', async ({ email }: { email: string }, thunkApi) => {
  const [state, access_token] = getStateAndToken(thunkApi);
  const isMaintenance = state.app.isMaintenance;

  if (isMaintenance) {
    console.log('Maintenance mode active - skipping checkEmailExist');
    // Return mock data that indicates guest user doesn't exist in DB
    return {
      data: {
        is_guest: true,
        exists_in_db: false,
        is_team_member: false,
      },
      status: 204,
    };
  }

  try {
    const { data, status } = await sendRequest('post', checkEmailExistUrl, { email }, token);
    return { data, status };
  } catch (error: any) {
    const errorMessage = error.response ? error.response.data.message : error.message;

    thunkApi.dispatch(
      setSnackbar({
        open: true,
        message: i18n.t('error.UserAlreadyExists', { email, interpolation: { escapeValue: false } }),
        type: 'error',
      }),
    );
    return thunkApi.rejectWithValue(errorMessage);
  }
});

const GUEST_EMAIL_DOMAIN = '@guest.printt';
const GUEST_DAYS = 14;

// A guest is signed in with a key kept in the guest_user cookie: its email is "<key>@guest.printt" and its password
// the key. When the cookie is gone or out of date but this browser is still signed in as a guest, the key is read back
// from that guest's email, so the same guest (and the files it uploaded) is signed in again rather than a new one
export const guestKey = (currentUser?: { email?: string } | null) => {
  const cookieKey = Cookies.get('guest_user');
  const expiry = Cookies.get('guest_user_expiry');
  if (cookieKey && !(expiry && new Date(expiry) < new Date())) {
    return cookieKey;
  }

  const signedInKey = currentUser?.email?.endsWith(GUEST_EMAIL_DOMAIN)
    ? currentUser.email.slice(0, -GUEST_EMAIL_DOMAIN.length)
    : null;
  const key = signedInKey || Math.random().toString(32).substring(2, 15);
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + GUEST_DAYS);
  Cookies.set('guest_user', key, { expires: GUEST_DAYS, secure: true, sameSite: 'Strict' });
  Cookies.set('guest_user_expiry', expiryDate.toISOString(), { expires: GUEST_DAYS, secure: true, sameSite: 'Strict' });
  return key;
};

export const createGuestUser = createAsyncThunk('user/createGuest', async (_, thunkApi) => {
  const [state, access_token] = getStateAndToken(thunkApi);

  const isMaintenance = state.app.isMaintenance;
  if (isMaintenance) {
    return;
  } else {
    if (isGuestUserCreationInProgress) {
      return;
    }

    isGuestUserCreationInProgress = true;
    try {
      const guest_user = guestKey(state.user.currentUser);

      let userData = null;

      const body: GuestUser = {
        email: `${guest_user}${GUEST_EMAIL_DOMAIN}`,
        password: `${guest_user}`,
        first_name: 'Guest',
        last_name: 'User',
        is_guest: true,
        country: i18n.language === 'bg' ? 'BG' : 'UK',
      };

      await thunkApi.dispatch(checkEmailExist({ email: body.email })).then(async (res: any) => {
        const { data, status } = res.payload;

        // The API's field is exists_in_db. Reading exist_in_db (never set) made every returning guest register
        // again, which the API refuses with "Email address …@guest.printt is already registered"
        const { is_guest, exists_in_db } = data;
        if (is_guest && !exists_in_db) {
          const { data, status } = await sendRequest('post', registerUrl, body, token);
          thunkApi.dispatch(setIsNewUser(true));
          userData = data;
        } else {
          const { data, status } = await sendRequest(
            'post',
            loginUrl,
            { email: body?.email, password: body?.password },
            token,
          );
          thunkApi.dispatch(setIsNewUser(false));
          userData = data;
        }
      });

      return userData;
    } catch (error: any) {
      const errorMessage = error.response ? error.response.data.message : error.message;
      return thunkApi.rejectWithValue(errorMessage);
    } finally {
      isGuestUserCreationInProgress = false;
    }
  }
});

export const registerUser = createAsyncThunk('user/create', async (formData: CreateUserType, thunkApi) => {
  const [state, access_token] = getStateAndToken(thunkApi);

  const isMaintenance = state.app.isMaintenance;
  if (isMaintenance) {
    return;
  } else {
    try {
      const body = {
        email: `${formData?.email}`,
        password: formData?.password,
        first_name: formData?.firstName,
        last_name: formData?.lastName,
        phone_number: formData?.phoneNumber,
        isGuest: formData?.is_guest,
        country: formData?.country,
      };

      const { data, status } = await sendRequest('post', registerUrl, body, token);
      if (status === 200) {
        const loginData = {
          email: formData?.email || '',
          password: formData?.password || '',
        };
        thunkApi.dispatch(loginUser(loginData)).then((res: any) => {
          thunkApi.dispatch(getUser());
        });
      }
      return data;
    } catch (error: any) {
      const errorMessage = error.response ? error.response.data.message : error.message;
      // The API prefixes the field ("email:user with this email address already exists."): an account with that
      // email gets the sign-in message, anything else is shown without the prefix
      const message = /already exists/i.test(String(errorMessage))
        ? i18n.t('error.UserAlreadyExists', { email: formData?.email, interpolation: { escapeValue: false } })
        : i18n.t(String(errorMessage).replace(/^\w+:\s*/, ''));

      thunkApi.dispatch(setSnackbar({ open: true, message, type: 'error' }));
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
});

// Website: Google auth code (string). Native apps: { idToken } from the Google SDK.
// A popup's code, a native app's ID token, or (phones) a code from a whole-page sign-in with the address Google sent
// the page back to
type GoogleLoginInput = string | { idToken: string } | { code: string; redirectUri: string };

export const loginGoogle = createAsyncThunk('user/loginGoogle', async (code: GoogleLoginInput, thunkApi) => {
  try {
    const body =
      typeof code === 'string'
        ? { code }
        : 'idToken' in code
          ? { id_token: code.idToken }
          : { code: code.code, redirect_uri: code.redirectUri };
    const { data } = await sendRequest('post', loginGoogleUrl, body, token);

    await thunkApi.dispatch(checkEmailExist({ email: data.email })).then(async (res: any) => {
      if (res.payload.status === 204) {
        thunkApi.dispatch(setIsNewUser(true));
      } else {
        thunkApi.dispatch(setIsNewUser(false));
      }
    });
    thunkApi.dispatch(setSnackbar({ open: true, message: i18n.t('login.successfulLogin'), type: 'success' }));
    return data;
  } catch (error: any) {
    thunkApi.dispatch(setSnackbar({ open: true, message: i18n.t('login.loginFailed'), type: 'error' }));
    return thunkApi.rejectWithValue(error?.response?.data);
  }
});

export const appleLogin = createAsyncThunk('user/appleLogin', async (loginResponse: any, thunkApi) => {
  try {
    const { data } = await sendRequest(
      'post',
      window.isMobileApp ? loginAppleMobile : loginAppleUrl,
      {
        token: loginResponse.authorization.id_token,
        // Apple only sends the name on the first sign-in
        ...(loginResponse.user?.name?.firstName && { first_name: loginResponse.user.name.firstName }),
        ...(loginResponse.user?.name?.lastName && { last_name: loginResponse.user.name.lastName }),
      },
      token,
    );
    await thunkApi.dispatch(checkEmailExist({ email: data.email })).then(async (res: any) => {
      if (res.payload.status === 204) {
        thunkApi.dispatch(setIsNewUser(true));
      } else {
        thunkApi.dispatch(setIsNewUser(false));
      }
    });
    thunkApi.dispatch(setSnackbar({ open: true, message: i18n.t('login.successfulLogin'), type: 'success' }));
    return data;
  } catch (error: any) {
    return thunkApi.rejectWithValue(error?.response?.data);
  }
});

export const loginUser = createAsyncThunk('user/login', async (formData: LoginUserType, thunkApi) => {
  const [state, access_token] = getStateAndToken(thunkApi);

  const isMaintenance = state.app.isMaintenance;
  if (isMaintenance) {
    return;
  } else {
    try {
      const body = {
        email: formData.email,
        password: formData.password,
      };
      await thunkApi.dispatch(checkEmailExist({ email: body.email })).then(async (res: any) => {
        if (res.payload.status === 204) {
          thunkApi.dispatch(setIsNewUser(true));
        } else {
          thunkApi.dispatch(setIsNewUser(false));
        }
      });

      const { data, status } = await sendRequest('post', loginUrl, body, token);
      if (status === 201) {
        await thunkApi.dispatch(getUser());
      }
      thunkApi.dispatch(setSnackbar({ open: true, message: i18n.t('login.successfulLogin'), type: 'success' }));
      return data;
    } catch (error: any) {
      const errorMessage = error.response.data.detail;
      thunkApi.dispatch(setSnackbar({ open: true, message: i18n.t('error.wrongCredentials'), type: 'error' }));
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
});

// Forgets the signed-in user on this device and starts a guest session, as after signing out
const clearSignedInUser = (dispatch: (action: any) => any) => {
  isGuestUserCreationInProgress = false;
  dispatch(clearAppSlice());
  dispatch(clearOrderSlice());
  dispatch(clearFileSlice());
  dispatch(clearTeamSlice());
  dispatch(clearUserSlice());
  dispatch(createGuestUser());
};

export const logoutUser = createAsyncThunk('user/logout', async (_, thunkApi) => {
  try {
    const [state, access_token] = getStateAndToken(thunkApi);
    const { data } = await sendRequest(
      'post',
      logoutUrl,
      { client_id: settings.client_id, token },
      `Bearer ${access_token}`,
    );
    clearSignedInUser(thunkApi.dispatch);
    thunkApi.dispatch(setSnackbar({ open: true, message: i18n.t('messages.userLogoutSuccess'), type: 'success' }));

    return data;
  } catch (error: any) {
    const errorMessage = error.response ? error.response.data.message : error.message;
    return thunkApi.rejectWithValue(errorMessage);
  }
});

export const getUser = createAsyncThunk('user/currentUser', async (_, thunkApi) => {
  if (isGetUserCreationInProgress) {
    return;
  }

  isGetUserCreationInProgress = true;

  try {
    const [state, access_token] = getStateAndToken(thunkApi);
    const { user_id } = state.user.currentUser;

    const { data } = await sendRequest('get', `${userUrl}/${user_id}`, null, `Bearer ${access_token}`);
    return data;
  } catch (error: any) {
    const errorMessage = error.response ? error.response.data.message : error.message;
    return thunkApi.rejectWithValue(errorMessage);
  } finally {
    isGetUserCreationInProgress = false;
  }
});

export const updateUser = createAsyncThunk('user/updateUser', async (formData: ProfileType, thunkApi) => {
  const [state, access_token] = getStateAndToken(thunkApi);
  const { user_id } = state.user.currentUser;

  const body = {
    first_name: formData.first_name,
    last_name: formData.last_name,
    country: formData.country,
    phone_number: formData.phone_number,
  };

  try {
    const { data, status } = await sendRequest('put', `${userUrl}/${user_id}`, body, `Bearer ${access_token}`);
    thunkApi.dispatch(setSnackbar({ open: true, message: i18n.t('messages.profileUpdatedSuccess'), type: 'success' }));

    return data;
  } catch (error: any) {
    const errorMessage = error.response.data.detail;
    thunkApi.dispatch(setSnackbar({ open: true, message: i18n.t('messages.userUpdateFailure'), type: 'error' }));
    return thunkApi.rejectWithValue(errorMessage);
  }
});
export const updateUserPassword = createAsyncThunk(
  'user/updateUserPassword',
  async (formData: PasswordType, thunkApi) => {
    const [state, access_token] = getStateAndToken(thunkApi);
    const { user_id } = state.user.currentUser;

    const body = {
      current_password: formData.current_password,
      new_password: formData.password,
      confirm_new_password: formData.confirmPassword,
    };

    try {
      const { data, status } = await sendRequest('put', `${userUrl}/${user_id}`, body, `Bearer ${access_token}`);
      thunkApi.dispatch(
        setSnackbar({ open: true, message: i18n.t('messages.passwordUpdatedSuccess'), type: 'success' }),
      );

      return data;
    } catch (error: any) {
      const errorMessage = error.response.data.detail;
      thunkApi.dispatch(setSnackbar({ open: true, message: i18n.t('messages.userUpdateFailure'), type: 'error' }));
      return thunkApi.rejectWithValue(errorMessage);
    }
  },
);

export const deleteUser = createAsyncThunk('user/updateUser', async (_, thunkApi) => {
  const [state, access_token] = getStateAndToken(thunkApi);
  const { currentUser } = state.user;

  try {
    const { data } = await sendRequest('delete', `${userUrl}/${currentUser?.user_id}/`, null, `Bearer ${access_token}`);
    // The account's sign-in went with it, so there's nothing left to sign out of on the server: only forget it here
    clearSignedInUser(thunkApi.dispatch);
    thunkApi.dispatch(setSnackbar({ open: true, message: i18n.t('service.accountDeleted'), type: 'success' }));
    return data;
  } catch (error: any) {
    const errorMessage = error.response.data.detail;
    return thunkApi.rejectWithValue(errorMessage);
  }
});

export const resetUserPassword = createAsyncThunk('user/updateUser', async ({ email }: { email: string }, thunkApi) => {
  const [state, access_token] = getStateAndToken(thunkApi);
  try {
    const { data } = await sendRequest('post', resetPasswordUrl, { email }, token);
    // Shows the address typed, so a typo is seen straight away
    const message = i18n.t('password.resetPassInstruction', { email, interpolation: { escapeValue: false } });
    thunkApi.dispatch(setSnackbar({ open: true, message, type: 'success' }));
    return data;
  } catch (error: any) {
    const errorMessage = error.response.data.detail;
    thunkApi.dispatch(setSnackbar({ open: true, message: i18n.t('password.resetPasswordError'), type: 'error' }));
    return thunkApi.rejectWithValue(errorMessage);
  }
});
